<template>
  <div class="map-container-wrapper">
    <div id="amap-container"></div>
    <MapLegend />
  </div>
</template>

<script>
import AMapLoader from '@amap/amap-jsapi-loader';
import amapConfig from '../../config/amapConfig';
import MapLegend from './MapLegend.vue';

export default {
  name: 'HotelMap',
  components: { MapLegend },
  props: {
    hotelList: {
      type: Array,
      default: () => []
    },
    halls: { type: Array, default: () => [] },             
    selectedExhibition: { type: Object, default: () => null }   
  },
  data() {
    return {
      // map 实例不放在 data 中，避免 Vue 劫持导致性能问题和报错
    };
  },
  created() {
    this.map = null;
    this.points = []; // 用于存储当前地图上的所有 Marker
    this.hallMarkers = []; // 用于管理展馆 Marker 实例
  },
  watch: {
    // 深度监听数据变化
    hotelList: {
      handler(newVal) {
        if (this.map && newVal) {
          this.$nextTick(() => {
            this.updateMarkers();
          });
        }
      },
      deep: true
    },
    selectedExhibition: {
      handler(newVal) {
        if (this.map) 
          this.updateHallMarkers(newVal);
      },
      deep: true
    },
    // ★★★ 新增：监听展馆列表变化 ★★★
    halls: {
      handler(newVal) {
        if (this.map && newVal) {
          // 1. 重绘展馆图标
          this.updateHallMarkers();
          
          // 2. 视野飞到新城市的展馆群
          // 如果有展馆标记，自动调整视野以包含所有展馆
          if (this.hallMarkers && this.hallMarkers.length > 0) {
             this.map.setFitView(this.hallMarkers, false, [50, 50, 50, 50]);
          }
        }
      },
      deep: true
    }
  },
  mounted() {
    this.initMap();
  },
  beforeDestroy() {
    if (this.map) {
      this.map.destroy();
    }
  },
  methods: {
    initMap() {

      AMapLoader.load({
        key: amapConfig.key,
        version: "2.0",
        plugins: ['AMap.ToolBar', 'AMap.Scale','AMap.MarkerCluster'], 
      }).then((AMap) => {
        this.map = new AMap.Map("amap-container", {
          viewMode: "",
          zoom: amapConfig.zoom,
          center: amapConfig.center,
          mapStyle: 'amap://styles/whitesmoke',
        });

        this.map.addControl(new AMap.Scale());
        this.map.addControl(new AMap.ToolBar());

        // 添加展馆中心点
        this.updateHallMarkers();

        // 如果初始有数据，渲染 Marker
        if (this.hotelList.length > 0) {
          this.updateMarkers();
        }
      }).catch(e => {
        console.error("地图加载失败:", e);
      });
    },

    // 渲染所有展馆标记
    updateHallMarkers(currentExh) {
     if (!this.map || !window.AMap || !this.halls) return;

      // 1. 清除旧标记
      if (this.hallMarkers && this.hallMarkers.length > 0) {
        this.map.remove(this.hallMarkers);
      }
      this.hallMarkers = [];

      // 2. 确定当前选中的展会 (优先用传进来的参数，没有则用 props)
      const targetExh = currentExh || this.selectedExhibition;
      
      let activeHallId = null;
      if (targetExh && targetExh.HallId) {
        activeHallId = targetExh.HallId;
        console.log('Map: 当前激活的 HallId 是:', activeHallId);
      } else {
        console.log('Map: 当前没有选中的展会或 HallId 为空');
      }

      // 3. 遍历渲染
      this.halls.forEach(hall => {
        // 强制类型转换比较，防止一个是字符串 "1" 一个是数字 1
        const isActive = activeHallId && (String(activeHallId) === String(hall.Id));
        
        const content = isActive 
          ? `<div class="hall-marker active">📍 ${hall.Name}</div>`
          : `<div class="hall-marker">🏢 ${hall.Name}</div>`;

        const marker = new window.AMap.Marker({
          position: [hall.Longitude, hall.Latitude],
          content: content,
          offset: new window.AMap.Pixel(-30, -15),
          zIndex: isActive ? 999 : 800,
          extData: hall
        });

        this.hallMarkers.push(marker);
        this.map.add(marker);

      });
    },

    // 核心：点聚合渲染逻辑 + 视野调整
    updateMarkers() {
      if (!this.map || !window.AMap) return;

      // 1. 清理旧聚合
      if (this.cluster) {
        this.cluster.setMap(null);
        this.cluster = null;
      }

      const points = [];
      // 用数组暂存所有有效坐标，用于计算边界
      const validLngs = [];
      const validLats = [];

      // 2. 处理酒店数据
      this.hotelList.forEach(hotel => {
        const lat = parseFloat(hotel.lat || hotel.Latitude);
        const lng = parseFloat(hotel.lng || hotel.Longitude);

        // 过滤无效坐标
        if (!lat || !lng || isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return;

        const plainHotelData = JSON.parse(JSON.stringify(hotel));

        points.push({
          lnglat: [lng, lat], 
          extData: plainHotelData
        });

        // 收集坐标
        validLngs.push(lng);
        validLats.push(lat);
      });

      // 3. 生成聚合 (仅当有酒店数据时)
      if (points.length > 0) {
        this.cluster = new window.AMap.MarkerCluster(this.map, points, {
          gridSize: 60,
          maxZoom: 15,
          renderMarker: this.renderSingleMarker,
          renderClusterMarker: this.renderClusterMarker
        });

        this.cluster.on('click', (e) => {
          if (!e.clusterData || e.clusterData.length <= 1) {
             const hotel = e.clusterData && e.clusterData.length > 0 ? e.clusterData[0].extData : e.marker.getExtData();
             if(hotel) this.$emit('hotel-click', hotel);
          }
        });
      }

      // 4. ★★★ 核心修复：计算边界逻辑 ★★★
      
      // 如果选中了展会，把目标展馆的坐标也加入计算序列
      if (this.selectedExhibition && this.selectedExhibition.HallId && this.halls) {
        const targetHall = this.halls.find(h => h.Id === this.selectedExhibition.HallId);
        if (targetHall && targetHall.Longitude && targetHall.Latitude) {
           validLngs.push(parseFloat(targetHall.Longitude));
           validLats.push(parseFloat(targetHall.Latitude));
        }
      } else {
        // 如果没有选展会，加入默认中心点，防止空列表时报错或无视野
        validLngs.push(amapConfig.center[0]);
        validLats.push(amapConfig.center[1]);
      }

      // 5. 构造 Bounds 并调整视野
      if (validLngs.length > 0 && validLats.length > 0) {
        // 计算西南角(最小值) 和 东北角(最大值)
        const minLng = Math.min(...validLngs);
        const maxLng = Math.max(...validLngs);
        const minLat = Math.min(...validLats);
        const maxLat = Math.max(...validLats);

        const southWest = [minLng, minLat];
        const northEast = [maxLng, maxLat];

        // 构造合法的 Bounds 对象
        const bounds = new window.AMap.Bounds(southWest, northEast);

        // 设置视野，[上, 右, 下, 左] padding
        this.map.setBounds(bounds, false, [50, 50, 50, 460]);
      }
    },

     //渲染单个酒店标记 
    renderSingleMarker(context) {
      const hotel = context.data[0].extData;
      
      let className = 'custom-marker';
      let zIndex = 100;
      if (hotel.walletBalance > 0) {
        className += ' urgent';
        zIndex = 200;
      } else if (hotel.isMain) {
        className += ' main';
        zIndex = 150;
      }

     

      const content = `
      <div class="${className} combo-marker">
        <!-- 左侧：酒店名称 (辅助信息) -->
        <div class="marker-name">${hotel.HotelNameCn}</div>
        
        <!-- 右侧：价格 (视觉重心) -->
        <div class="marker-price-box">
          <span class="price">¥${hotel.minPrice}</span>
          ${hotel.walletBalance > 0 ? '<span class="badge">💰</span>' : ''}
        </div>
        
        <!-- 底部箭头 -->
        <div class="arrow"></div>
      </div>
    `;

      context.marker.setContent(content);
      context.marker.setOffset(new window.AMap.Pixel(-20, -45));
      context.marker.setzIndex(zIndex);
      context.marker.setExtData(hotel); // 辅助
    },

    // 渲染聚合点 (圆圈)
    renderClusterMarker(context) {
      const count = context.count;
      const markers = context.clusterData;
      
      // 智能逻辑：如果聚合圈里包含"预存"酒店，整个圈变红
      let colorClass = 'normal-cluster';
      let hasUrgent = false;
      let hasMain = false;

      for (let item of markers) {
        const h = item.extData;
        if (h.walletBalance > 0) { hasUrgent = true; break; }
        if (h.isMain) hasMain = true;
      }

      if (hasUrgent) colorClass = 'urgent-cluster';
      else if (hasMain) colorClass = 'main-cluster';

      const div = document.createElement('div');
      div.className = `custom-cluster ${colorClass}`;
      div.innerHTML = count;
      
      context.marker.setContent(div);
      context.marker.setOffset(new window.AMap.Pixel(-20, -20));
    },

    // 供父组件调用：定位到指定点
    focusLocation(lat, lng) {
      if (this.map && !isNaN(lat) && !isNaN(lng)) {
        // 建议 600-800ms，这个时长视觉上最舒服
        const duration = 800;
        const position = [parseFloat(lng), parseFloat(lat)];

        // 18级是散开聚合的最佳级别
        this.map.setZoom(18, false, duration);
        this.map.panTo(position, duration);

        
      }
    }
  }
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/dashboard/map.scss";
</style>

<style lang="scss">
@use "@/assets/styles/dashboard/global-marker.scss";
</style>